import { useQueryClient } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import axiosInstance from "@/axiosConfig";
import { ACCOUNTING_ENDPOINTS } from "@/features/accounting/configs/endpoints";
import { ACCOUNTING_QUERY_KEYS } from "@/features/accounting/configs/queryKeys";
import BaseDrawer from "@/shared/components/Drawer";
import FileUploaderButton from "@/shared/components/FileUploaderButton/FileUploaderButton";
import { useJobContext } from "@/shared/contexts/JobContext";
import { type FileFormData, fileSchema } from "@/shared/schemas/fileSchema";
import { zodFormResolver } from "@/shared/schemas/zodFormResolver";
import { postFile } from "@/shared/services/directUploads.service";
import { postJob } from "@/shared/services/jobs.service";
import type { Job } from "@/shared/types/job.types";
import { Button } from "@/shared/ui/Button";
import { Separator } from "@/shared/ui/Separator";
import { getApiUrl } from "@/shared/utils/api";

type ImportCategoriesDrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Absent depuis la page Budget : le modèle est alors servi par la route org-scoped. */
  bankMovementId?: string
  zIndex?: number
};

const ImportCategoriesDrawer = ({
  open, onOpenChange, bankMovementId, zIndex,
}: ImportCategoriesDrawerProps) => {
  const { openJobModal, onJobComplete } = useJobContext();
  const queryClient = useQueryClient();

  const form = useForm<FileFormData>({
    resolver: zodFormResolver(fileSchema),
    mode: "onChange",
  });

  const createUploadFileJob = async (file: File): Promise<Job> => {
    const { signedId } = await postFile(file);
    const { data: { job } } = await postJob({
      type: "UPLOAD FICHIER CATEGORIES",
      signedId,
    });
    return job;
  };

  const onImportFileSubmit = async ({ file }: FileFormData) => {
    try {
      const createdJob = await createUploadFileJob(file);
      openJobModal(createdJob);
      onJobComplete(createdJob, () => {
        queryClient.invalidateQueries({ queryKey: [...ACCOUNTING_QUERY_KEYS.ledgerAllocations, "operation-types"] });
        // ADM-621 : l'import alimente aussi la page Budget et la synthèse.
        queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.orgOperationTypes });
        queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.budgets });
        queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.synthesis });
      });
      onOpenChange(false);
    }
    catch {
      toast.error("Erreur lors du lancement de l'import");
    }
  };

  const downloadTemplate = async () => {
    try {
      const templateUrl = bankMovementId
        ? ACCOUNTING_ENDPOINTS.LEDGER_ALLOCATION_OPERATION_TYPES_IMPORT_TEMPLATE(bankMovementId)
        : ACCOUNTING_ENDPOINTS.ORG_OPERATION_TYPES_IMPORT_TEMPLATE;
      const response = await axiosInstance.get(
        getApiUrl(templateUrl),
        { responseType: "blob" },
      );
      const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "modele-import-categories.xlsx";
      link.click();
      URL.revokeObjectURL(link.href);
    }
    catch {
      toast.error("Erreur lors du téléchargement du modèle");
    }
  };

  return (
    <BaseDrawer
      drawerId="DRW-IMPCAT"
      open={open}
      onOpenChange={onOpenChange}
      isFirstLevel={false}
      onBack={() => onOpenChange(false)}
      width="893px"
      zIndex={zIndex ?? 75}
      title="Importer des catégories"
      description="Ajoutez plusieurs catégories d'un coup via un fichier Excel."
    >
      <Separator className="my-4" />
      <ol className="list-decimal list-inside space-y-6 mt-4 text-sm">
        <li>
          <span className="ml-5 -mt-[1.5em] block">
            Téléchargez un modèle (2 colonnes :
            {" "}
            <code className="font-semibold">budget</code>
            {" "}
            et
            {" "}
            <code className="font-semibold">categorie</code>
            ).
            <Button variant="outline" className="mt-2 block" onClick={downloadTemplate}>
              <Download className="h-4 w-4 mr-1.5 inline" />
              Télécharger le modèle
            </Button>
          </span>
        </li>

        <li>
          Complétez le fichier :
          <ul className="list-disc list-inside ml-6 mt-2 space-y-1 text-xs text-slate-600">
            <li>
              <span className="font-semibold">Colonne A (budget)</span>
              {" "}
              :
              {" "}
              <code>ASC</code>
              {" "}
              ou
              {" "}
              <code>AEP</code>
            </li>
            <li>
              <span className="font-semibold">Colonne B (categorie)</span>
              {" "}
              : nom de la catégorie
            </li>
            <li>Les catégories déjà existantes (même budget + même nom) seront ignorées</li>
            <li>Les lignes avec un budget invalide seront rejetées et listées dans le rapport d'erreurs</li>
          </ul>
        </li>

        <li>
          Chargez votre fichier Excel (.xlsx) :
          <FormProvider {...form}>
            <FileUploaderButton<FileFormData> onSubmit={onImportFileSubmit} />
          </FormProvider>
        </li>
      </ol>
    </BaseDrawer>
  );
};

export default ImportCategoriesDrawer;
